import { SoftDeleteFlag, Timestamps, Versioned } from './shared.types';

//help.loyverse.com/help/how-discounts-are-calculated

export enum DiscountType {
  FIXED_PERCENT = 'FIXED_PERCENT',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  VARIABLE_PERCENT = 'VARIABLE_PERCENT',
  VARIABLE_AMOUNT = 'VARIABLE_AMOUNT',
  DISCOUNT_BY_POINTS = 'DISCOUNT_BY_POINTS',
}

/**
 * How a discount reaches a sale.
 * - AUTOMATIC: the POS applies it to every sale of the assigned products
 *   (each product belongs to at most one automatic discount; membership
 *   enables allow_below_cost_sale).
 * - MANUAL: a predefined discount (e.g. "10MT", "5%") the cashier picks per
 *   cart line at checkout. Products subscribe to it (many-to-many); it is
 *   never applied automatically and never touches allow_below_cost_sale.
 */
export enum DiscountApplication {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
}

export interface Discount extends Timestamps, Versioned {
  id: string; // UUID. Optional for creation, required for update.
  type: DiscountType; // Discount type.
  name: string; // Discount name. Length between 1 and 40 characters.
  discount_amount?: number; // Discount value in money, required only for FIXED_AMOUNT.
  discount_percent?: number; // Discount value in percentage, required only for FIXED_PERCENT.
  store_id: string;
  restricted_access?: boolean; // If true, password verification is required to apply.
  /** Absent on rows stored before this field existed: treated as AUTOMATIC. */
  application?: DiscountApplication;
  /**
   * Optional ISO deadline. Once it passes the discount can no longer be
   * applied (calculation rejects it, receipts record a failed check) and
   * the expiry sweeper turns allow_below_cost_sale back off on every
   * product in product_ids. Null/absent = no deadline.
   */
  valid_until?: string | null;
  /**
   * Store products assigned to this discount. For AUTOMATIC discounts each
   * product can belong to at most one discount and membership enables
   * allow_below_cost_sale (removing membership disables it). For MANUAL
   * discounts this is the list of products the cashier may apply it to.
   */
  product_ids?: string[];
  /**
   * Merchant the discount's store belongs to. Required for product
   * assignments so the discount service can call the products service.
   */
  merchant_id?: string;
  /**
   * User who configured the deadline. The expiry sweeper acts as this
   * user when resetting product flags, so the audit trail attributes the
   * change to the person who scheduled it.
   */
  deadline_set_by?: string | null;
  /**
   * Set by the expiry sweeper once the product flags for this deadline
   * have been reset, so a deadline is processed exactly once. Cleared
   * whenever a new valid_until is set.
   */
  expiry_processed_at?: string | null;
  /** Number of consecutive failed attempts to clean up expired products. */
  expiry_attempt_count?: number;
  /** Set after cleanup reaches its retry limit and needs manager attention. */
  expiry_failed_at?: string | null;
  /** Last cleanup error, shown to managers when automatic cleanup gives up. */
  expiry_error?: string | null;
}

/** Immutable discount details stored on product audit records. */
export interface DiscountAuditSnapshot {
  id: string;
  name: string;
  type: DiscountType;
  discount_amount?: number;
  discount_percent?: number;
  valid_until?: string | null;
}

export enum ProductDiscountChangeAction {
  ASSIGNED = 'ASSIGNED',
  REASSIGNED = 'REASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
  UPDATED = 'UPDATED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  PRODUCT_PRICE_UPDATED = 'PRODUCT_PRICE_UPDATED',
}

/** Structured metadata sent to the products service for history and realtime. */
export interface ProductDiscountChange {
  action: ProductDiscountChangeAction;
  previous_discount?: DiscountAuditSnapshot | null;
  discount?: DiscountAuditSnapshot | null;
  occurred_at: string;
}

export interface DiscountPage {
  discounts: Discount[];
  count: number;
  cursor?: string;
}

export interface CreateDiscount {
  type: DiscountType;
  name: string;
  discount_amount?: number;
  discount_percent?: number;
  store_id: string;
  restricted_access?: boolean;
  /** Defaults to AUTOMATIC when absent. */
  application?: DiscountApplication;
  valid_until?: string | null;
  /** Accepted only for MANUAL discounts (the products the cashier may apply it to). */
  product_ids?: string[];
  merchant_id?: string;
  deadline_set_by?: string | null;
  updator_id?: string;
}

export interface UpdateDiscount {
  id: string;
  type?: DiscountType;
  name?: string;
  discount_amount?: number;
  discount_percent?: number;
  store_id: string;
  restricted_access?: boolean;
  /** Must equal the stored value; the application of a discount cannot change. */
  application?: DiscountApplication;
  /**
   * Set a new deadline (ISO string), or null to remove the deadline.
   * Setting a non-null deadline clears expiry_processed_at so the sweeper
   * treats it as a fresh deadline.
   */
  valid_until?: string | null;
  /**
   * Rejected on update: replacing the list would discard concurrent changes.
   * Send subscribe_product_ids / unsubscribe_product_ids instead.
   */
  product_ids?: string[];
  /** Manual discounts only: merged into the subscribed products on the server. */
  subscribe_product_ids?: string[];
  unsubscribe_product_ids?: string[];
  merchant_id?: string;
  deadline_set_by?: string | null;
  updator_id?: string;
}

/** Assigns one product to at most one discount, or removes its assignment. */
export interface SetProductDiscount {
  store_id: string;
  merchant_id: string;
  product_id: string;
  discount_id: string | null;
  updator_id: string;
}

export interface ProductDiscountAssignment {
  product_id: string;
  discount_id: string | null;
}

export interface DiscountDBItem extends Discount {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /** Sparse expiry index; present only while a deadline is unprocessed. */
  GSI8PK?: string;
  GSI8SK?: string;
}

export interface GetDiscountById {
  id: string;
  include_deleted?: SoftDeleteFlag;
}

export interface GetDiscounts {
  store_id: string;
  limit?: number;
  cursor?: string;
  include_deleted?: SoftDeleteFlag;
  /**
   * Manual (cashier-applied) discounts are only returned when true, so clients
   * built before they existed cannot apply them automatically.
   */
  include_manual?: boolean;
}

export interface DiscountPage {
  discounts: Discount[];
  cursor?: string;
  count: number;
}

export enum CalculateDiscountCommands {
  PERCENTAGE = 'PERCENTAGE', // Apply a percentage discount to the item or receipt
  FIXED = 'FIXED', // Apply a fixed amount discount to the item or receipt
  MULTIPLE_DISCOUNTS = 'MULTIPLE_DISCOUNTS', // Apply multiple discounts (percentage + fixed) to the item or receipt
  ITEM_LEVEL_DISCOUNT = 'ITEM_LEVEL_DISCOUNT', // Apply item-level discounts where each item can have its own discounts
  RECEIPT_LEVEL_DISCOUNT = 'RECEIPT_LEVEL_DISCOUNT', // Apply receipt-level discount across all items in the receipt
  TAX_AFTER_DISCOUNT = 'TAX_AFTER_DISCOUNT', // Apply tax after discounts have been applied
  TAX_BEFORE_DISCOUNT = 'TAX_BEFORE_DISCOUNT', // Apply tax before discounts are applied
}

export interface CalculateDiscount {
  item_price: number;
  discount_ids: string[];
  added_discount_id: string;
  included_discount_ids: string[];
  command: CalculateDiscountCommands;
}

export interface CalculateDiscountResultItem {
  // The list of items, used for item-level discounts
  price: number; // Original price of the item
  finalPrice: number; // Final price of the item after applying discounts
  discount_value?: number; // Total discount applied to this item
  discount_ids: string[]; // List of discount IDs applied to the item
}

export interface CalculateDiscountResult {
  discount_command: CalculateDiscountCommands; // The command that was used for this discount calculation
  item_price?: number; // The original item price (for single-item discounts)
  final_price?: number; // The final price after discounts (and possibly taxes) have been applied
  discount_value?: number; // The total amount discounted
  total_discount?: number; // The total discount applied across all items or to the receipt
  total_tax?: number; // The total tax applied (used in tax-before/after scenarios)
  items?: CalculateDiscountResultItem[]; // The list of items with their final prices and discounts
}
